import { useState } from 'react';
import { FaPlus, FaTrash } from 'react-icons/fa6';
import { FiRefreshCw } from 'react-icons/fi';
import { formatDate } from '../../../../utils/collateralFormatters';
import useLegalCaseAgreements, { type AddLegalAgreementValue } from '../../../../hooks/useLegalCaseAgreements';
import {
    LEGAL_AGREEMENT_TERMINAL_STATUSES,
    LegalAgreementStatus,
    type LegalAgreement,
    type SelectOption,
} from '../../../../types/LegalCase';
import LegalCaseAgreementForm from './LegalCaseAgreementForm';
import ConfirmDialog from './ConfirmDialog';

interface LegalCaseAgreementsProps {
    caseId: string;
    initialAgreements?: LegalAgreement[];
}

// Cor do selo por status do acordo. Quitado em verde (fim feliz); em atraso/executado em
// vermelho (inadimplência/execução); substituído em cinza (renegociado, fora de jogo);
// formalização/cumprimento em âmbar (em curso).
const SETTLED = new Set<LegalAgreementStatus>([LegalAgreementStatus.SETTLED]);
const TROUBLED = new Set<LegalAgreementStatus>([
    LegalAgreementStatus.OVERDUE,
    LegalAgreementStatus.EXECUTED,
]);
const INACTIVE = new Set<LegalAgreementStatus>([LegalAgreementStatus.SUPERSEDED]);

const statusClass = (status: LegalAgreementStatus): string => {
    if (SETTLED.has(status)) {
        return 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300';
    }
    if (TROUBLED.has(status)) {
        return 'bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300';
    }
    if (INACTIVE.has(status)) {
        return 'bg-gray-200 dark:bg-slate-700 text-gray-600 dark:text-gray-300';
    }
    return 'bg-amber-100 dark:bg-amber-900 text-amber-700 dark:text-amber-300';
};

const isTerminal = (status: LegalAgreementStatus): boolean =>
    LEGAL_AGREEMENT_TERMINAL_STATUSES.includes(status);

// SUPERSEDED nunca é opção de mudança manual de status — superseder exige o acordo sucessor
// (operação própria, botão Renegociar). Filtra do dropdown as opções vindas do page-form.
const changeableStatusOptions = (options: SelectOption[]): SelectOption[] =>
    options.filter((o) => o.value !== LegalAgreementStatus.SUPERSEDED);

// Uma linha de acordo. Extraída para fora do componente (diretriz: sem função dentro de
// função). Acordo não-terminal mostra o dropdown de status e o botão de renegociar; o
// substituído mostra que apontou para um sucessor. As opções de status vêm do hook (page-form
// do backend, com fallback local) — não de uma constante fixa aqui.
const AgreementItem = ({
    agreement,
    statusOptions,
    onChangeStatus,
    onSupersede,
    onRemove,
    disabled,
}: {
    agreement: LegalAgreement;
    statusOptions: SelectOption[];
    onChangeStatus: (status: LegalAgreementStatus) => void;
    onSupersede: () => void;
    onRemove: () => void;
    disabled: boolean;
}) => {
    const terminal = isTerminal(agreement.status);
    return (
        <li className="flex items-start justify-between gap-2 py-2 border-b border-gray-100 dark:border-slate-700 last:border-0">
            <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-gray-800 dark:text-gray-100">
                        {agreement.agreedAmountFormatted || 'Valor a confirmar'}
                    </span>
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium ${statusClass(agreement.status)}`}>
                        {agreement.statusFormatted}
                    </span>
                    {agreement.signedAt && (
                        <span className="text-[10px] text-gray-500 dark:text-gray-400">
                            Assinado em {formatDate(agreement.signedAt)}
                        </span>
                    )}
                </div>
                {agreement.status === LegalAgreementStatus.SUPERSEDED && agreement.supersededByAgreementId && (
                    <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">
                        Substituído por acordo posterior
                    </div>
                )}
                {agreement.description && (
                    <p className="text-sm text-gray-600 dark:text-gray-300 mt-0.5 whitespace-pre-wrap break-words">{agreement.description}</p>
                )}
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
                {!terminal && (
                    <select
                        aria-label="Mudar status do acordo"
                        value={agreement.status}
                        disabled={disabled}
                        onChange={(e) => onChangeStatus(e.target.value as LegalAgreementStatus)}
                        className="appearance-none text-xs rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-200 pl-2 pr-6 py-1 disabled:opacity-50 bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20fill%3D%22none%22%20viewBox%3D%220%200%2024%2024%22%20stroke%3D%22%236b7280%22%3E%3Cpath%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%20stroke-width%3D%222%22%20d%3D%22M19%209l-7%207-7-7%22/%3E%3C/svg%3E')] bg-[length:0.85rem] bg-[right_0.35rem_center] bg-no-repeat"
                    >
                        {changeableStatusOptions(statusOptions).map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                    </select>
                )}
                {!terminal && (
                    <button
                        type="button"
                        onClick={onSupersede}
                        disabled={disabled}
                        title="Renegociar (novo acordo substitui este)"
                        className="p-1.5 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900 rounded-lg transition-colors disabled:opacity-50"
                    >
                        <FiRefreshCw size={12} />
                    </button>
                )}
                <button
                    type="button"
                    onClick={onRemove}
                    disabled={disabled}
                    title="Remover acordo"
                    className="p-1.5 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900 rounded-lg transition-colors disabled:opacity-50"
                >
                    <FaTrash size={11} />
                </button>
            </div>
        </li>
    );
};

// Seção "Acordos" do detalhe do caso. Lista os acordos do mais antigo ao mais novo, cada um
// com seu ciclo de status (formalização → cumprimento → quitado; com atraso e execução). A
// renegociação cria um acordo sucessor e marca o atual como substituído (versionamento por
// superseção — preserva a cadeia). A remoção passa por ConfirmDialog (padrão do módulo).
const LegalCaseAgreements = ({ caseId, initialAgreements }: LegalCaseAgreementsProps) => {
    const agreements = useLegalCaseAgreements(caseId, initialAgreements);
    const [showAddForm, setShowAddForm] = useState(false);
    const [supersedingId, setSupersedingId] = useState<string | null>(null);
    const [pendingRemoval, setPendingRemoval] = useState<string | null>(null);

    const openAddForm = () => {
        agreements.clearErrors();
        setSupersedingId(null);
        setShowAddForm(true);
    };

    const closeForms = () => {
        agreements.clearErrors();
        setShowAddForm(false);
        setSupersedingId(null);
    };

    const openSupersedeForm = (agreementId: string) => {
        agreements.clearErrors();
        setShowAddForm(false);
        setSupersedingId(agreementId);
    };

    const submitAdd = async (value: AddLegalAgreementValue): Promise<boolean> => {
        const ok = await agreements.addAgreement(value);
        if (ok) closeForms();
        return ok;
    };

    const submitSupersede = async (value: AddLegalAgreementValue): Promise<boolean> => {
        if (!supersedingId) return false;
        const ok = await agreements.supersedeAgreement(supersedingId, {
            agreedAmount: value.agreedAmount,
            signedAt: value.signedAt,
            description: value.description,
        });
        if (ok) closeForms();
        return ok;
    };

    const confirmRemoval = async () => {
        if (!pendingRemoval) return;
        await agreements.removeAgreement(pendingRemoval);
        setPendingRemoval(null);
    };

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between">
                <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Acordos
                </h4>
                {!showAddForm && supersedingId === null && (
                    <button
                        type="button"
                        onClick={openAddForm}
                        className="flex items-center gap-1 px-2 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-xs font-medium"
                    >
                        <FaPlus size={10} /> Acordo
                    </button>
                )}
            </div>

            {agreements.agreements.length === 0 && !showAddForm && (
                <p className="text-sm text-gray-400 dark:text-gray-500">Nenhum acordo registrado</p>
            )}

            {agreements.agreements.length > 0 && (
                <ul>
                    {agreements.agreements.map((agreement) => (
                        <AgreementItem
                            key={agreement.id}
                            agreement={agreement}
                            statusOptions={agreements.statusOptions}
                            onChangeStatus={(status) => agreements.changeStatus(agreement.id, status)}
                            onSupersede={() => openSupersedeForm(agreement.id)}
                            onRemove={() => setPendingRemoval(agreement.id)}
                            disabled={agreements.isSaving}
                        />
                    ))}
                </ul>
            )}

            {showAddForm && (
                <LegalCaseAgreementForm
                    errorFields={agreements.errorFields}
                    isSaving={agreements.isSaving}
                    onSubmit={submitAdd}
                    onCancel={closeForms}
                />
            )}

            {supersedingId !== null && (
                <div className="space-y-2">
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                        Novo acordo (substitui o anterior, que fica marcado como substituído)
                    </p>
                    <LegalCaseAgreementForm
                        errorFields={agreements.errorFields}
                        isSaving={agreements.isSaving}
                        submitLabel="Renegociar"
                        onSubmit={submitSupersede}
                        onCancel={closeForms}
                    />
                </div>
            )}

            <ConfirmDialog
                open={pendingRemoval !== null}
                title="Remover acordo"
                message="Remover este acordo do caso? Esta ação não pode ser desfeita."
                isBusy={agreements.isSaving}
                onConfirm={confirmRemoval}
                onCancel={() => setPendingRemoval(null)}
            />
        </div>
    );
};

export default LegalCaseAgreements;
